import React, { useEffect, useMemo, useState } from 'react';

import { calculateRowTotals } from '../../utils/calculations';
import { getBgClass, getIndentClass } from '../../utils/styleUtils';

import ExpenseMilestoneData from './ExpenseMilestoneData';
import UncommittedRow from './UncommittedRow';

import { formatFigure, formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

export default function UncommittedSection(props) {
    const [showUncommittedChildren, setShowUncommittedChildren] = useState(false);

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        if (t.batch === 'uncommitted' || t.batch === 'projects uncommitted') {
            setShowUncommittedChildren(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    // Calculate indentation level
    const indentLevel = props.indentLevel;
    const childIndentLevel = indentLevel + 1;

    const toggleUncommittedShow = () => {
        setShowUncommittedChildren(!showUncommittedChildren);
    };

    const uncommitted = props.expense.uncommitted;
    if (!uncommitted) return null;

    const { maxExpenseMilestonesCount } = props;

    const totals = useMemo(() => {
        return calculateRowTotals(uncommitted.currentMilestones);
    }, [uncommitted.currentMilestones]);

    return (
        <>
            {(!props.isProjectExpense || (props.isProjectExpense && props.expense.name !== 'Unallocated')) && (
                <tr className={getBgClass(indentLevel, 'expense')}>
                    <td>
                        <div
                            className={`tw-flex tw-items-center tw-gap-x-1.5 tw-cursor-pointer ${getIndentClass(indentLevel)}`}
                            onClick={toggleUncommittedShow}
                        >
                            <div className="tw-font-medium tw-text-gray-700">{$t('Uncommitted')}</div>
                            <div>{showUncommittedChildren ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                        </div>
                    </td>
                    <td className="tw-p-4"></td>
                    <td className="tw-p-4 tw-text-right tw-text-gray-700 tw-border-r-2">
                        {formatFigure(uncommitted.value, formatToFixedMoneyWithLocale, { precision: 2 })}
                    </td>
                    {uncommitted.currentMilestones &&
                        [...Array(maxExpenseMilestonesCount)].map((e, i) => {
                            return (
                                <React.Fragment key={`uncommitted_milestones_${i}`}>
                                    <ExpenseMilestoneData
                                        key={`${props.keyPrefix || ''}uncommitted_${props.expense.id}_milestone_${i}`}
                                        milestones={uncommitted.currentMilestones}
                                        milestoneId={i}
                                        keyPrefix={props.keyPrefix || ''}
                                        amountOnly={true}
                                        isCommitted={false}
                                    />
                                </React.Fragment>
                            );
                        })}
                    <td className="tw-p-4 tw-text-right"></td>
                    <td className="tw-p-4 tw-text-right">{formatFigure(totals.totalValue, formatToFixedMoneyWithLocale, { precision: 2 })}</td>
                    <td className="tw-p-4 tw-text-right"></td>
                </tr>
            )}

            {(showUncommittedChildren || (props.isProjectExpense && props.expense.name === 'Unallocated')) && (
                <>
                    {/* Unallocated can have both cash and VIK - show separate rows when both exist */}
                    {props.isProjectExpense &&
                    props.expense.name === 'Unallocated' &&
                    uncommitted.milestones &&
                    typeof uncommitted.milestones === 'object' &&
                    !Array.isArray(uncommitted.milestones) ? (
                        <>
                            {uncommitted.milestones.cash?.length > 0 && (
                                <UncommittedRow
                                    {...props}
                                    key={`uncommitted_cash_${props.expense.id}`}
                                    type="cash"
                                    label="Cash"
                                    data={{
                                        ...uncommitted,
                                        milestones: uncommitted.milestones.cash || [],
                                    }}
                                    periods={props.periods}
                                    indentLevel={childIndentLevel}
                                    expense={props.expense}
                                    totalUncommittedValue={(uncommitted.value || 0) - (uncommitted.vik || 0)}
                                />
                            )}
                            {uncommitted.milestones.vik?.length > 0 && (
                                <UncommittedRow
                                    {...props}
                                    key={`uncommitted_vik_${props.expense.id}`}
                                    type="vik"
                                    label="VIK"
                                    data={{
                                        ...uncommitted,
                                        milestones: uncommitted.milestones.vik || [],
                                    }}
                                    periods={props.periods}
                                    indentLevel={childIndentLevel}
                                    expense={props.expense}
                                    totalUncommittedValue={uncommitted.vik || 0}
                                />
                            )}
                        </>
                    ) : (
                        /* Expense milestones are single type, show them directly */
                        <UncommittedRow
                            {...props}
                            key={`uncommitted_${props.expense.id}`}
                            type={uncommitted.is_vik ? 'vik' : 'cash'}
                            label={uncommitted.is_vik ? 'VIK' : 'Cash'}
                            data={uncommitted}
                            periods={props.periods}
                            indentLevel={childIndentLevel}
                            expense={props.expense}
                            totalUncommittedValue={uncommitted.value}
                        />
                    )}
                </>
            )}
        </>
    );
}
